import type { PixelEventName } from '@xshop/shared';
import { injectScript, type BrowserPixel, type PixelPayload } from './loader';

const SCRIPT_SRC = 'https://analytics.tiktok.com/i18n/pixel/events.js';
const SCRIPT_ID = 'xshop-tiktok-pixel';

// TikTok names two of the five funnel events differently from Meta; the server mapper agrees.
const TIKTOK_EVENTS: Record<PixelEventName, string> = {
  PageView: 'Pageview',
  ViewContent: 'ViewContent',
  AddToCart: 'AddToCart',
  InitiateCheckout: 'InitiateCheckout',
  Purchase: 'CompletePayment'
};

// The methods TikTok's own snippet stubs out so calls survive until the SDK loads.
const QUEUED_METHODS = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'] as const;

// The queueing stub, matching the shape TikTok's SDK expects to find and drain on load.
interface Ttq {
  (...args: unknown[]): void;
  _i: Record<string, unknown[]>;
  _t: Record<string, number>;
  _o: Record<string, unknown>;
  methods: readonly string[];
  push: (args: unknown[]) => void;
  load?: (id: string) => void;
  page?: () => void;
  track?: (event: string, properties?: Record<string, unknown>, options?: Record<string, unknown>) => void;
  [method: string]: unknown;
}

type TtqWindow = Window & { ttq?: Ttq; TiktokAnalyticsObject?: string };

// Installs the ttq stub if no other tag already did, queueing every call until the SDK arrives.
function ensureTtq(): Ttq {
  const target = window as TtqWindow;
  if (target.ttq) return target.ttq;

  const queue: unknown[][] = [];
  const ttq = ((...args: unknown[]) => { queue.push(args); }) as Ttq;
  ttq._i = {};
  ttq._t = {};
  ttq._o = {};
  ttq.methods = QUEUED_METHODS;
  ttq.push = (args) => { queue.push(args); };
  for (const method of QUEUED_METHODS) {
    ttq[method] = (...args: unknown[]) => { queue.push([method, ...args]); };
  }
  target.ttq = ttq;
  target.TiktokAnalyticsObject = 'ttq';
  return ttq;
}

// Builds the properties TikTok reads off a value-bearing event.
function tiktokProperties(payload: PixelPayload): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  if (payload.currency) properties.currency = payload.currency;
  if (payload.value !== undefined) properties.value = payload.value;
  if (payload.contentId) properties.content_id = payload.contentId;
  if (payload.contentId) properties.content_type = 'product';
  return properties;
}

// The TikTok browser pixel; event_id matches the server dispatch so TikTok deduplicates them.
export const tiktokPixel: BrowserPixel = {
  // Loads one SDK bundle per pixel code, which is how TikTok scopes its instances.
  install(ids) {
    const ttq = ensureTtq();
    for (const id of ids) {
      injectScript(`${SCRIPT_SRC}?sdkid=${encodeURIComponent(id)}&lib=ttq`, `${SCRIPT_ID}-${id}`);
      ttq.load?.(id);
    }
    ttq.page?.();
  },

  // Fires one event under TikTok's own name for it, carrying the shared deduplication id.
  track(event, payload) {
    const target = window as TtqWindow;
    if (event === 'PageView') return; // install() already called ttq.page() for this view
    target.ttq?.track?.(TIKTOK_EVENTS[event], tiktokProperties(payload), { event_id: payload.eventId });
  }
};
